import React, { FC, memo } from "react";
import Icon from "components/Icon/Icon";
import PackageTypeIcon from "components/PackageTypeIcon/PackageTypeIcon";
import Tooltip from "components/Tooltip/Tooltip";
import { IconType } from "enums/Icon";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

interface PackageMarkerProps {
  packageType: string;
  hasCustomsDeclarations: boolean;
}

const Marker = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

// Hangs outside the marker's box, so the marker and the weight after it keep
// their position whether or not a row shows the customs icon.
const CustomsSlot = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 100%;
  margin-right: 6px;
  display: flex;
  align-items: center;
`;

const CustomsIcon = styled(Icon)`
  width: 14px;
  height: 14px;
  background-color: ${({ theme }) => theme.packageCustomsBg};
`;

// The box / bag marker of a storage row, with a receipt icon hung to its left
// when the shipment has a customs declaration.
const PackageMarker: FC<PackageMarkerProps> = ({
  packageType,
  hasCustomsDeclarations,
}) => {
  const { t } = useTranslation("common");
  const customsLabel = t("tooltips.shipmentHasCustomsDeclarations");

  return (
    <Marker>
      {hasCustomsDeclarations && (
        <CustomsSlot
          role="img"
          aria-label={customsLabel}
          data-testid="customs-declaration-icon"
        >
          <Tooltip
            body={customsLabel}
            place="bottom"
            openEvents={{
              click: isMobile,
              mouseover: !isMobile,
              focus: !isMobile,
            }}
          >
            <CustomsIcon type={IconType.ReceiptFill} />
          </Tooltip>
        </CustomsSlot>
      )}
      <PackageTypeIcon type={packageType} />
    </Marker>
  );
};

export default memo(PackageMarker);
